import {ButtonGroup} from "@luciad/ria-sample-common/components/ButtonGroup";
import {Modal} from "@luciad/ria-sample-common/components/Modal";
import {useMapContext} from "@luciad/ria-sample-common/hooks/useMapContext";
import {IToastContext, useToastContext} from "@luciad/ria-sample-common/hooks/useToast";
import {RasterDataType} from "@luciad/ria/model/tileset/RasterDataType";
import {RasterSamplingMode} from "@luciad/ria/model/tileset/RasterSamplingMode";
import {getReference} from "@luciad/ria/reference/ReferenceProvider";
import {createBounds} from "@luciad/ria/shape/ShapeFactory";
import {Map} from "@luciad/ria/view/Map";
import {WebGLMap} from "@luciad/ria/view/WebGLMap";
import React from "react";
import {addLayer} from "../DataFormatsUtil";
import {EarthquakesPainter} from "../EarthquakesPainter";
import {GeoJsonDataLoader} from "../loaders/GeoJsonDataLoader";
import {GMLDataLoader} from "../loaders/GMLDataLoader";
import {KMLDataLoader} from "../loaders/KMLDataLoader";
import {LuciadFusionDataLoader} from "../loaders/LuciadFusionDataLoader";
import {OGC3DTilesDataLoader} from "../loaders/OGC3DTilesDataLoader";
import {WFSDataLoader} from "../loaders/WFSDataLoader";
import {WMSDataLoader} from "../loaders/WMSDataLoader";
import {WMTSDataLoader} from "../loaders/WMTSDataLoader";
import {Button} from "@luciad/ria-sample-common/components/button/Button";

interface ExampleDataButton {
  text: string;
  title: string;
  action: (map: Map, toastContext: IToastContext) => Promise<void>;
  disableFor2D?: boolean;
  disableForSW?: boolean;
}

const WEB_MERCATOR = getReference("EPSG:3857");
const CRS84 = getReference("CRS:84");

const EXAMPLE_DATA_BUTTONS: ExampleDataButton[] = [
  {
    text: "GeoJson airspaces",
    title: "Add US airspaces from a simple GeoJson file",
    action: async (map: Map) => {
      const layer = GeoJsonDataLoader.createLayer("Airspaces", "/sampledata/airspaces_us.json");
      await addLayer(map, layer);
    }
  },
  {
    text: "GML Earthquakes",
    title: "Add earthquake points from a GML file",
    action: async (map: Map) => {
      const layer = GMLDataLoader.createLayer(
          "Earthquakes",
          "/sampledata/gml/Events2000toPres.xml",
          new EarthquakesPainter(),
          ["EPSG:4326"]
      );
      await addLayer(map, layer);
    }
  },
  {
    text: "KML buildings",
    title: "Add San Francisco building and others from a KML file",
    action: async (map: Map) => {
      const layer = KMLDataLoader.createLayer("Twin Peaks", "/sampledata/kml/twinpeaks.kml");
      await addLayer(map, layer);
    }
  },
  {
    text: "WMS rivers",
    title: "Add rasterized rivers served by a LuciadFusion WMS server, which supports GetFeatureInfo",
    action: async (map: Map) => {
      const bounds = createBounds(WEB_MERCATOR, [
        -18341377.587492295,
        10794860.301886301,
        2979920.55623243,
        8223592.484710306
      ]);
      const layer = WMSDataLoader.createLayerBasic(
          "https://sampleservices.luciad.com/wms",
          "rivers",
          true,
          true,
          bounds
      );
      await addLayer(map, layer);
    }
  },
  {
    text: "WFS rivers",
    title: "Add rivers served by a LuciadFusion WFS server",
    action: async (map: Map) => {
      const layer = WFSDataLoader.createLayer("https://sampleservices.luciad.com/wfs", "usrivers", "US Rivers");
      await addLayer(map, layer);
    }
  },
  {
    text: "WMTS black marble",
    title: "Add Black Marble imagery served by a LuciadFusion WMTS server",
    action: async (map: Map) => {
      const layer = WMTSDataLoader.createLayer(
          "https://sampleservices.luciad.com/wmts",
          "92c09725-a9c5-46fb-bffd-d9e23b4abbf2",
          "Black marble",
          {
            reference: WEB_MERCATOR
          });
      await addLayer(map, layer);
    }
  },
  {
    text: "LuciadFusion black marble",
    title: "Add Black Marble imagery served by a LuciadFusion LTS tile server",
    action: async (map: Map) => {
      const layer = LuciadFusionDataLoader.createLayer("Black marble", {
        reference: WEB_MERCATOR,
        bounds: createBounds(WEB_MERCATOR,
            [-20037508.34278925, 40075016.6855785, -20037508.34278925, 40075016.6855785]),
        level0Columns: 1,
        level0Rows: 1,
        levelCount: 8,
        url: "https://sampleservices.luciad.com/lts",
        coverageId: "92c09725-a9c5-46fb-bffd-d9e23b4abbf2",
        tileWidth: 256,
        tileHeight: 256
      });
      await addLayer(map, layer);
    }
  },
  {
    text: "LuciadFusion elevation",
    title: "Add elevation data served by a LuciadFusion WMTS server",
    disableFor2D: true,
    disableForSW: true,
    action: async (map: Map) => {
      const layer = LuciadFusionDataLoader.createLayer("Elevation", {
        reference: CRS84,
        bounds: createBounds(CRS84, [-180, 360, -90, 180]),
        level0Columns: 4,
        level0Rows: 2,
        levelCount: 24,
        url: "https://sampleservices.luciad.com/lts",
        coverageId: "e8f28a35-0e8c-4210-b2e8-e5d4333824ec",
        tileWidth: 32,
        tileHeight: 32,
        dataType: RasterDataType.ELEVATION,
        samplingMode: RasterSamplingMode.POINT
      });
      await addLayer(map, layer);
    }
  },
  {
    text: "OGC 3D tiles",
    title: "Add sample OGC 3D Tiles Mesh dataset to map",
    disableForSW: true,
    action: async (map: Map, toastContext: IToastContext) => {
      const layer = OGC3DTilesDataLoader.createLayer(
          "https://sampleservices.luciad.com/ogc/3dtiles/marseille-mesh/tileset.json",
          0.8,
          toastContext.showInfoToast
      );
      await addLayer(map, layer);
    }
  }
];

interface Props {
  show: boolean;
  onHide: () => void;
}

export const AddExampleDataModal = ({show, onHide}: Props) => {
  const {map} = useMapContext();
  const toastContext = useToastContext();

  const handleButtonClick = (button: ExampleDataButton) => {
    onHide();
    button.action(map, toastContext).catch((error) => {
      toastContext.showErrorToast(error.message ?? "Error occured while adding layer", error);
    });
  };

  return (
      <Modal show={show} title="Add example data" onHide={onHide}>
        <ButtonGroup vertical={true}>
          {EXAMPLE_DATA_BUTTONS.map((button) => {
            const mapIs2D = map.reference.identifier !== "EPSG:4978";
            const mapIsSW = !(map instanceof WebGLMap);
            let title = button.title;
            let disabled = false;
            if (button.disableFor2D && mapIs2D) {
              disabled = true;
              title = "Feature not available on 2D maps";
            }
            if (button.disableForSW && mapIsSW) {
              disabled = true;
              title = "Feature is only available on WebGLMap";
            }
            return (
                <Button
                    key={button.text}
                    disabled={disabled}
                    title={title}
                    onClick={() => handleButtonClick(button)}
                >
                  {button.text}
                </Button>
            )
          })}
        </ButtonGroup>
      </Modal>
  );
}